// macro-frag-eve-prompt — Profi-Format-Prompt für „Frag EVE dazu"-Buttons
// auf Wirtschafts-Releases.
//
// Doc-Feedback 2026-05-17: das standardmäßige „analysiere die Faktenlage live"
// produziert wieder Prosa-Geschwafel. Wir müssen den Output strikt vorgeben.
//
// Output-Format: Markdown mit Tabellen (MarkdownText-Renderer kann das seit
// 2026-05-17). Resultat ist eine kompakte Pre-Trade-Briefing-Card in der
// EVE-Chat-Bubble — 1:1 Profi-Standard wie auf der /trading/macro Page.

export interface MacroFragEveInput {
  eventName: string;
  /** YYYY-MM-DD */
  date: string;
  /** Berlin-Zeit „HH:MM". */
  timeBerlin: string;
  /** ET-Zeit „HH:MM". */
  timeEt: string;
  /** Optional Country-Code (z.B. „US", „DE", „EU"). */
  country?: string;
  consensus?: string | number | null;
  previous?: string | number | null;
  actual?: string | number | null;
}

function fmtVal(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return v.toLocaleString('de-DE', { maximumFractionDigits: 2 });
  return String(v) || '—';
}

export function buildMacroFragEvePrompt(input: MacroFragEveInput): string {
  const consensus = fmtVal(input.consensus);
  const previous = fmtVal(input.previous);
  const actual = input.actual !== null && input.actual !== undefined ? fmtVal(input.actual) : null;
  const country = input.country ? input.country.toUpperCase() : '';

  return `MACRO PRE-TRADE-BRIEFING für Doc (Profi-Trader in Berlin, eigenes Risk-Buch).

DATEN:
- Event: ${input.eventName}${country ? ` (${country})` : ''}
- Datum: ${input.date}
- Zeit: ${input.timeBerlin} (${input.timeEt} ET)
- Prognose: ${consensus}
- Vorher: ${previous}
${actual ? `- Aktuell: ${actual}\n` : ''}
HARTE FORMAT-VORGABEN (sonst ist die Antwort wertlos):
- Antworte in MARKDOWN mit Tabellen (GFM-Pipe-Syntax). KEIN freier Fließtext.
- KEIN „dürfte/könnte/möglicherweise". Pro Aussage MUSS eine Zahl, Schwelle
  oder konkrete Richtung dabei sein.
- Wahrscheinlichkeiten in % (Summe der 3 Szenarien = 100 ±3).
- „Eingepreist?" explizit beantworten mit konkreter Markt-Preis-Begründung
  (Fed-Funds-Futures, OIS, Polymarket, EUR-Swaps — je nachdem was passt).
- Asset-Reaktionen für DXY · EUR/USD · BTC · NDX · SPX · DAX · Gold · US10Y · Öl.
- Magnitudes: Aktien/FX/Commodities in % (z.B. „+0.5 %"), US10Y in
  Basispunkten (z.B. „+8 bp"). Bei flat einfach „flat".
- Fazit max 2-3 Sätze, mit konkreten Levels (SPX 7400 = Cluster-Support etc.),
  KEINE allgemeinen Anlage-Tipps.
- Live-Search: ziehe aktuelle Konsensus-Werte, Markt-Preise, Polymarket-
  Probabilities wenn ein passender Markt existiert.

VORLAGE (passe Werte konkret an, behalte die Struktur):

## ${input.eventName} · ${input.date} · ${input.timeBerlin} (${input.timeEt} ET)
**Prognose:** ${consensus} | **Vorher:** ${previous} | **Eingepreist:** [konkret aus Markt-Preisen]

### Szenarien

| Szenario | Wahrsch. | Schwelle | Mechanismus |
|---|---|---|---|
| Übertrifft | XX % | konkrete Zahl | 1 Satz |
| Wie erwartet | XX % | range | 1 Satz |
| Verfehlt | XX % | konkrete Zahl | 1 Satz |

### Asset-Reaktionen

| Asset | Übertrifft | Wie erwartet | Verfehlt |
|---|---|---|---|
| DXY | ±X.X % | ±X.X % | ±X.X % |
| EUR/USD | ±X.X % | ±X.X % | ±X.X % |
| BTC | ±X.X % | ±X.X % | ±X.X % |
| NDX | ±X.X % | ±X.X % | ±X.X % |
| SPX | ±X.X % | ±X.X % | ±X.X % |
| DAX | ±X.X % | ±X.X % | ±X.X % |
| Gold | ±X.X % | ±X.X % | ±X.X % |
| US10Y | ±X bp | ±X bp | ±X bp |
| Öl | ±X.X % | ±X.X % | ±X.X % |

**Fazit:** 2-3 Sätze mit konkreten Levels und Trigger-Conditions.`;
}

export function buildMacroDisplayText(input: MacroFragEveInput): string {
  const country = input.country ? `${input.country.toUpperCase()} · ` : '';
  return `${country}${input.eventName} · ${input.date} ${input.timeBerlin} (${input.timeEt} ET)`;
}
